import { Refresh, Settings } from "@mui/icons-material";
import { Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import RedisIcon from "../../components/RedisIcon/RedisIcon";
import { useStoreActions } from "../../models/RootStore";
import {
  IQueueJob,
  IQueueJobDetails,
  IQueueJobsPage,
  IQueueScheduler,
  IQueueSummary,
  IRedisDetails,
  QueueSort,
  QueueTab
} from "../../models/Queues.types";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import QueueJobPopup from "./QueueJobPopup";
import QueueJobsTable from "./QueueJobsTable";
import QueuesSettingsPopup from "./QueuesSettingsPopup";
import QueuesTable from "./QueuesTable";
import RedisInfoPopup from "./RedisInfoPopup";
import { IQueuesSettings, loadQueuesSettings, saveQueuesSettings } from "./queuesSettings";
import useStyles from "./Queues.style";

// bull-board refreshes its Redis panel on this beat while it is open.
const REDIS_REFRESH_MS = 5000;

const emptyJobsPage: IQueueJobsPage = { totalCount: 0, page: 1, pageSize: 25, jobs: [] };

const compareQueues = (sort: QueueSort) => (a: IQueueSummary, b: IQueueSummary) => {
  if (sort === "name-asc") {
    return a.name.localeCompare(b.name);
  }

  if (sort === "name-desc") {
    return b.name.localeCompare(a.name);
  }

  // By job count in a state, most first; ties keep alphabetical order.
  return (b.counts[sort] || 0) - (a.counts[sort] || 0) || a.name.localeCompare(b.name);
};

const Queues: React.FC<any> = (props: any) => {
  const classes: any = useStyles();

  const getQueues = useStoreActions((actions) => actions.queues.getQueues);
  const getJobs = useStoreActions((actions) => actions.queues.getJobs);
  const getSchedulers = useStoreActions((actions) => actions.queues.getSchedulers);
  const getRedisInfo = useStoreActions((actions) => actions.queues.getRedisInfo);
  const getJob = useStoreActions((actions) => actions.queues.getJob);
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  const [queues, setQueues] = useState<IQueueSummary[]>([]);
  const [initializing, setInitializing] = useState<boolean>(true);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  const [settings, setSettings] = useState<IQueuesSettings>(loadQueuesSettings);
  const [showSettings, setShowSettings] = useState<boolean>(false);

  const [showRedis, setShowRedis] = useState<boolean>(false);
  const [redisDetails, setRedisDetails] = useState<IRedisDetails | null>(null);

  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const [status, setStatus] = useState<QueueTab>("failed");
  const [schedulers, setSchedulers] = useState<IQueueScheduler[]>([]);
  const [schedulersTotal, setSchedulersTotal] = useState<number>(0);
  const [queueSort, setQueueSort] = useState<QueueSort>("name-asc");
  const [page, setPage] = useState<number>(0);
  const pageSize = settings.pageSize;
  const [jobsPage, setJobsPage] = useState<IQueueJobsPage>(emptyJobsPage);
  const [loadingJobs, setLoadingJobs] = useState<boolean>(false);

  const [selectedJob, setSelectedJob] = useState<IQueueJob | null>(null);
  const [jobDetails, setJobDetails] = useState<IQueueJobDetails | null>(null);

  // Guards against an earlier, slower request overwriting a newer one.
  const jobsRequestIdRef = useRef<number>(0);
  // Same, for the queue list, the job details popup and the Redis popup.
  const queuesRequestIdRef = useRef<number>(0);
  const jobRequestIdRef = useRef<number>(0);
  const redisRequestIdRef = useRef<number>(0);
  // A failing backend would toast on every automatic refresh, so only the first failure speaks.
  const failureShownRef = useRef<{ [source: string]: boolean }>({});

  // One toast per source until it recovers; a refresh the user asked for always speaks.
  const reportFailure = (source: string, message: string, force = false) => {
    if (force || !failureShownRef.current[source]) {
      failureShownRef.current[source] = true;
      addMessage({ message });
    }
  };

  const loadQueues = useCallback(async (force = false) => {
    queuesRequestIdRef.current++;
    const requestId = queuesRequestIdRef.current;

    try {
      const result = await getQueues();

      if (requestId === queuesRequestIdRef.current) {
        failureShownRef.current.queues = false;
        setQueues(result);
        // A selection that no longer exists (queue gone, or a stale value) falls back to none.
        setSelectedQueue((current) => (current && result.some((queue) => queue.id === current) ? current : null));
        setLoadedAt(Date.now());
      }
    } catch (err: any) {
      if (requestId === queuesRequestIdRef.current) {
        reportFailure("queues", err?.message || t`Failed to load queues`, force);
      }
    } finally {
      setInitializing(false);
    }
  }, []);

  const loadJobs = useCallback(
    async (queueId: string, nextStatus: QueueTab, nextPage: number, nextPageSize: number, silent = false) => {
      jobsRequestIdRef.current++;
      const requestId = jobsRequestIdRef.current;

      // Automatic refreshes update the rows in place instead of flashing the overlay.
      if (!silent) {
        setLoadingJobs(true);
      }

      try {
        if (nextStatus === "schedulers") {
          const result = await getSchedulers({ queueId, page: nextPage, pageSize: nextPageSize });

          if (requestId === jobsRequestIdRef.current) {
            failureShownRef.current.jobs = false;
            const lastPage = Math.max(0, Math.ceil(result.totalCount / nextPageSize) - 1);

            if (nextPage > lastPage) {
              // The queue shrank since this page was chosen; the page change reloads.
              setPage(lastPage);
            } else {
              setSchedulers(result.schedulers);
              setSchedulersTotal(result.totalCount);
            }
          }

          return;
        }

        const result = await getJobs({
          queueId,
          status: nextStatus,
          page: nextPage,
          pageSize: nextPageSize
        });

        if (requestId === jobsRequestIdRef.current) {
          failureShownRef.current.jobs = false;
          const lastPage = Math.max(0, Math.ceil(result.totalCount / nextPageSize) - 1);

          if (nextPage > lastPage) {
            // The queue shrank since this page was chosen; the page change reloads.
            setPage(lastPage);
          } else {
            setJobsPage(result);
          }
        }
      } catch (err: any) {
        if (requestId === jobsRequestIdRef.current) {
          // A silent refresh that fails leaves the last good rows up instead of blanking the table.
          if (!silent) {
            setJobsPage(emptyJobsPage);
            setSchedulers([]);
            setSchedulersTotal(0);
          }

          reportFailure("jobs", err?.message || t`Failed to load jobs`, !silent);
        }
      } finally {
        if (requestId === jobsRequestIdRef.current) {
          setLoadingJobs(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    loadQueues();
  }, []);

  useEffect(() => {
    if (selectedQueue) {
      loadJobs(selectedQueue, status, page, pageSize);
    }
  }, [selectedQueue, status, page, pageSize]);

  const handleSelectQueue = (id: string) => {
    if (id === selectedQueue) {
      return;
    }

    // The previous queue's rows must not show under the new queue's name while it loads.
    setJobsPage(emptyJobsPage);
    setSchedulers([]);
    setSchedulersTotal(0);
    setSelectedQueue(id);
    setPage(0);
  };

  const handleStatusChange = (nextStatus: QueueTab) => {
    setStatus(nextStatus);
    setPage(0);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    handleSettingsChange({ ...settings, pageSize: nextPageSize });
  };

  const handleSettingsChange = (nextSettings: IQueuesSettings) => {
    if (nextSettings.pageSize !== settings.pageSize) {
      setPage(0);
    }

    setSettings(nextSettings);
    saveQueuesSettings(nextSettings);
  };

  const handleOpenRedis = async () => {
    redisRequestIdRef.current++;
    const requestId = redisRequestIdRef.current;

    setShowRedis(true);
    setRedisDetails(null);

    try {
      const details = await getRedisInfo();

      if (requestId === redisRequestIdRef.current) {
        setRedisDetails(details);
      }
    } catch (err: any) {
      if (requestId === redisRequestIdRef.current) {
        setShowRedis(false);
        addMessage({ message: err?.message || t`Failed to load Redis details` });
      }
    }
  };

  useEffect(() => {
    if (!showRedis) {
      return;
    }

    let polling = false;
    const timer = setInterval(async () => {
      // A slow answer must not pile requests up behind it.
      if (polling) {
        return;
      }

      polling = true;
      const requestId = redisRequestIdRef.current;

      try {
        const details = await getRedisInfo();

        if (requestId === redisRequestIdRef.current) {
          setRedisDetails(details);
        }
      } catch (err) {
        // Keep showing the last figures; the next beat tries again.
      } finally {
        polling = false;
      }
    }, REDIS_REFRESH_MS);

    return () => clearInterval(timer);
  }, [showRedis]);

  const handleCloseRedis = () => {
    // Drops any response still in flight for a popup that is no longer open.
    redisRequestIdRef.current++;
    setShowRedis(false);
  };

  const handleRefresh = (silent = false) => {
    loadQueues(!silent);

    if (selectedQueue) {
      loadJobs(selectedQueue, status, page, pageSize, silent);
    }
  };

  // The interval always calls the latest refresh, so it never works from stale state.
  const refreshRef = useRef(handleRefresh);
  refreshRef.current = handleRefresh;

  useEffect(() => {
    if (!settings.refreshSeconds) {
      return;
    }

    const timer = setInterval(() => refreshRef.current(true), settings.refreshSeconds * 1000);

    return () => clearInterval(timer);
  }, [settings.refreshSeconds]);

  const handleViewJob = async (job: IQueueJob) => {
    if (!selectedQueue) {
      return;
    }

    jobRequestIdRef.current++;
    const requestId = jobRequestIdRef.current;

    setSelectedJob(job);
    setJobDetails(null);

    try {
      const details = await getJob({ queueId: selectedQueue, jobId: job.id });

      if (requestId === jobRequestIdRef.current) {
        setJobDetails(details);
      }
    } catch (err: any) {
      if (requestId === jobRequestIdRef.current) {
        setSelectedJob(null);
        addMessage({ message: err?.message || t`Failed to load the job` });
      }
    }
  };

  const handleCloseJob = () => {
    // Drops any response still in flight for the job being closed.
    jobRequestIdRef.current++;
    setSelectedJob(null);
    setJobDetails(null);
  };

  // An open job belongs to the selected queue; when the queue goes away, so does the job.
  useEffect(() => {
    if (selectedQueue) {
      return;
    }
    jobRequestIdRef.current++;
    setSelectedJob(null);
    setJobDetails(null);
  }, [selectedQueue]);

  const selectedSummary = queues.find((queue) => queue.id === selectedQueue);

  const sortedQueues = useMemo(() => [...queues].sort(compareQueues(queueSort)), [queues, queueSort]);

  const renderContent = () => {
    if (initializing) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    return (
      <>
        <div className={classes.actionsRow}>
          <Button variant="outlined" startIcon={<Refresh />} className={classes.actionButton} onClick={() => handleRefresh()}>
            {t`Refresh`}
          </Button>
          <Button variant="outlined" startIcon={<RedisIcon />} className={classes.actionButton} onClick={handleOpenRedis}>
            {t`Redis`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Settings />}
            className={classes.actionButton}
            onClick={() => setShowSettings(true)}
          >
            {t`Settings`}
          </Button>
          <FormControl size="small" className={classes.sortControl}>
            <InputLabel id="queues-sort-label">{t`Sort by`}</InputLabel>
            <Select labelId="queues-sort-label" label={t`Sort by`} value={queueSort} onChange={(event) => setQueueSort(event.target.value as QueueSort)}>
              <MenuItem value="name-asc">{t`Alphabetical (A-Z)`}</MenuItem>
              <MenuItem value="name-desc">{t`Alphabetical (Z-A)`}</MenuItem>
              <MenuItem value="failed">{t`Failed jobs`}</MenuItem>
              <MenuItem value="completed">{t`Completed jobs`}</MenuItem>
              <MenuItem value="active">{t`Active jobs`}</MenuItem>
              <MenuItem value="waiting">{t`Waiting jobs`}</MenuItem>
              <MenuItem value="delayed">{t`Delayed jobs`}</MenuItem>
            </Select>
          </FormControl>
          {!!loadedAt && (
            <Typography className={classes.updatedAt}>
              {t`Updated`} <strong>{moment(loadedAt).format("LTS")}</strong>
            </Typography>
          )}
        </div>

        <QueuesTable queues={sortedQueues} selectedQueue={selectedQueue} onSelectQueue={handleSelectQueue} />

        {!!selectedQueue && (
          <QueueJobsTable
            queueName={selectedSummary?.name ?? selectedQueue}
            queuePrefix={selectedSummary?.prefix}
            status={status}
            schedulers={schedulers}
            schedulersTotal={schedulersTotal}
            counts={selectedSummary?.counts || {}}
            jobs={jobsPage.jobs}
            totalCount={jobsPage.totalCount}
            page={page}
            pageSize={pageSize}
            loading={loadingJobs}
            onStatusChange={handleStatusChange}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            onViewJob={handleViewJob}
          />
        )}
      </>
    );
  };

  return (
    <ServiceNavigationBar title={t`Queues`} {...props}>
      <div className={classes.view}>{renderContent()}</div>

      {showRedis && <RedisInfoPopup details={redisDetails} onClose={handleCloseRedis} />}

      {showSettings && (
        <QueuesSettingsPopup
          settings={settings}
          onChange={handleSettingsChange}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!!selectedJob && !!selectedQueue && (
        <QueueJobPopup
          queueId={selectedQueue}
          queueName={selectedSummary?.name ?? selectedQueue}
          queuePrefix={selectedSummary?.prefix}
          jobId={selectedJob.id}
          job={jobDetails}
          onClose={handleCloseJob}
        />
      )}
    </ServiceNavigationBar>
  );
};

export default Queues;
